import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, UserPlus } from "lucide-react";
import { FRIEND_KEY } from "@/pages/FriendInvite";
import { Logo } from "@/components/brand/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/common/PasswordInput";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { AuthAside } from "@/pages/Login";
import { cn } from "@/lib/utils";

const CO_FIELDS = [["company", "Raison sociale", "text"], ["siren", "SIREN (9 chiffres)", "text"], ["sector", "Secteur d'activité", "text"], ["headcount", "Effectif", "number"], ["revenue", "Chiffre d'affaires annuel (€)", "number"]];

export default function Register() {
  const { register, reload } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const friend = params.get("ami") || sessionStorage.getItem(FRIEND_KEY);
  const { data: inviter } = useQuery({ queryKey: ["friend-invite", friend], queryFn: () => api.get(`/relatives/invite/${friend}`).then((r) => r.data), enabled: !!friend, retry: false });
  const [kind, setKind] = useState(params.get("type") === "entreprise" ? "business" : "personal");
  const [form, setForm] = useState({ name: "", email: "", password: "", company: "", siren: "", sector: "", headcount: "", revenue: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await register({ name: form.name, email: form.email, password: form.password, friend_token: inviter ? friend : null });
      sessionStorage.removeItem(FRIEND_KEY);
      if (kind === "business") {
        await api.post("/business/companies", { name: form.company, siren: form.siren, sector: form.sector, headcount: Number(form.headcount), revenue: Number(form.revenue) });
        await api.post("/me/onboarding/complete");
        await reload();
      }
      navigate(kind === "business" ? "/entreprise" : "/", { replace: true });
    } catch (err) { setError(formatApiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2" data-testid="register-page">
      <AuthAside />
      <main className="flex flex-col justify-center px-6 py-12 sm:px-12">
        <div className="mx-auto w-full max-w-sm fade-up">
          <div className="lg:hidden"><Logo size={40} /></div>
          <h2 className="mt-10 text-3xl font-extrabold lg:mt-0">Créer mon espace</h2>
          <p className="mt-2 text-sm text-muted-foreground">{kind === "business" ? "Gratuit. Votre espace CASHMY Entreprise est créé, vous pourrez inviter votre comptable." : "Gratuit. Un foyer est créé automatiquement, vous pourrez inviter vos proches."}</p>
          {inviter && <p className="mt-4 flex items-center gap-2 rounded-xl bg-success/15 px-3 py-2 text-sm font-semibold" data-testid="register-friend-banner"><UserPlus className="h-4 w-4" /> Vous serez ajouté aux amis de {inviter.name}</p>}
          <form className="mt-8 space-y-4" onSubmit={submit}>
            <div className="grid grid-cols-2 gap-1 rounded-full bg-muted p-1" data-testid="register-kind">
              {[["personal", "Particulier"], ["business", "Entreprise"]].map(([k, l]) => (
                <button key={k} type="button" onClick={() => setKind(k)} className={cn("rounded-full py-2 text-sm font-semibold transition-colors", kind === k ? "bg-background shadow" : "text-muted-foreground")} data-testid={`register-kind-${k}`}>{l}</button>
              ))}
            </div>
            <div><Label htmlFor="name">Prénom et nom</Label><Input id="name" value={form.name} onChange={set("name")} required minLength={2} className="mt-1.5 h-11 rounded-xl" data-testid="register-name-input" /></div>
            <div><Label htmlFor="email">Email</Label><Input id="email" type="email" value={form.email} onChange={set("email")} required className="mt-1.5 h-11 rounded-xl" data-testid="register-email-input" /></div>
            <div><Label htmlFor="password">Mot de passe (8 caractères min.)</Label><PasswordInput id="password" autoComplete="new-password" value={form.password} onChange={set("password")} required minLength={8} className="mt-1.5 h-11 rounded-xl" data-testid="register-password-input" /></div>
            {kind === "business" && CO_FIELDS.map(([k, l, type]) => (
              <div key={k}><Label htmlFor={k}>{l}</Label><Input id={k} type={type} min={type === "number" ? 0 : undefined} value={form[k]} onChange={set(k)} required pattern={k === "siren" ? "\\d{3} ?\\d{3} ?\\d{3}" : undefined} className="mt-1.5 h-11 rounded-xl" data-testid={`register-${k}-input`} /></div>
            ))}
            {error && <p className="text-sm text-destructive" role="alert" data-testid="register-error">{error}</p>}
            <Button type="submit" disabled={busy} className="h-11 w-full rounded-full" data-testid="register-submit-button">Créer mon compte<ArrowRight className="ml-2 h-4 w-4" /></Button>
          </form>
          <p className="mt-6 text-center text-sm text-muted-foreground">Déjà inscrit ? <Link to="/login" className="font-semibold text-foreground underline-offset-4 hover:underline" data-testid="go-login-link">Se connecter</Link></p>
        </div>
      </main>
    </div>
  );
}
